import ee from '../../services/earth-engine';
import { get_xap, get_xbp, get_xcp } from './getXr';

const do_ac = function (xap, xbp, xcp, image, band) {
  const toa = image.select(band).divide(10000);
  const y = toa.multiply(xap.select(band)).subtract(xbp.select(band));
  const sur = y.divide(y.multiply(xcp.select(band)).add(1));
  return sur;
};

export const get_sur = (imagemasked) => {
  const bands = ['B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'B8', 'B8A', 'B11', 'B12'];
  let image = imagemasked.select(bands).unmask(0);
  image = s2_add_angs(image);
  const projection = image.select('B2').projection();
  const crs = projection.crs();
  const vza = image.select('vza');
  const vaa = image.select('vaa');
  const sza = image.select('sza');
  const saa = image.select('saa');
  let raa = ee.Image(vaa).subtract(saa); //.rename('raa').double()
  const deg2rad = ee.Number(Math.PI).divide(ee.Number(180.0));
  const cos_sza = sza.multiply(deg2rad).cos().rename('cos_sza');
  const cos_vza = vza.multiply(deg2rad).cos().rename('cos_vza');
  const cos_raa = raa.multiply(deg2rad).cos().rename('cos_raa');
  raa = raa.where(raa.lt(0), raa.add(360)).divide(360).rename('raa');
  let tcwv = get_wv_prior(image).divide(8);
  let aot = get_aot_prior(image).divide(3);
  const tcwv_v006 = get_wv_prior_v6(image).divide(8);
  const aot_v006 = get_aot_prior_v6(image).divide(3);
  aot = ee.Image([aot, aot_v006]).reduce(ee.Reducer.firstNonNull()).rename('aot');
  tcwv = ee.Image([tcwv, tcwv_v006]).reduce(ee.Reducer.firstNonNull()).rename('tcwv');
  const tco3 = get_ozone_prior(image).rename('tco3');
  const ele = ee.Image('USGS/GMTED2010').select('be75').multiply(0.0001).rename('ele');
  const inp = ee.Image.cat([cos_sza, cos_vza, cos_raa, aot, tcwv, tco3, ele]).reproject(crs, null, 500);
  let xap = get_xap(inp);
  let xbp = get_xbp(inp);
  let xcp = get_xcp(inp);
  xap = xap
    .multiply(
      ee.Image([
        21.432022999999997, 19.288629999999998, 23.570948, 14.282488, 12.966325000000001, 11.919912, 9.017822, 8.096992,
        10.290405, 5.0013630000000004, 6.812678,
      ]),
    )
    .add(
      ee.Image([
        0.437156, 0.446027, 0.544881, 0.529572, 0.510017, 0.513989, 0.519447, 0.554209, 0.540255, 0.780309, 0.835003,
      ]),
    );
  xbp = xbp
    .multiply(
      ee.Image([
        8.068029, 6.088372, 4.780875, 3.54993, 3.238609, 3.042271, 2.80878, 2.4387809999999996, 2.599056, 0.895949,
        0.5270859999999999,
      ]),
    )
    .add(
      ee.Image([
        0.023714, 0.015876, 0.00949, 0.004827, 0.00382, 0.003152, 0.002511, 0.001704, 0.002037, 0.000146, 4.3e-5,
      ]),
    );
  xcp = xcp
    .multiply(
      ee.Image([
        0.25760399999999994, 0.22974, 0.230782, 0.222948, 0.218427, 0.211096, 0.201904, 0.185895, 0.19265,
        0.09445300000000001, 0.050554,
      ]),
    )
    .add(
      ee.Image([
        0.073024, 0.050022, 0.030634, 0.015767, 0.012464, 0.010273, 0.008168, 0.005514, 0.006588, 0.000191, 4.2e-5,
      ]),
    );
  const B1 = do_ac(xap, xbp, xcp, image, 'B1');
  const B2 = do_ac(xap, xbp, xcp, image, 'B2');
  const B3 = do_ac(xap, xbp, xcp, image, 'B3');
  const B4 = do_ac(xap, xbp, xcp, image, 'B4');
  const B5 = do_ac(xap, xbp, xcp, image, 'B5');
  const B6 = do_ac(xap, xbp, xcp, image, 'B6');
  const B7 = do_ac(xap, xbp, xcp, image, 'B7');
  const B8 = do_ac(xap, xbp, xcp, image, 'B8');
  const B8A = do_ac(xap, xbp, xcp, image, 'B8A');
  const B11 = do_ac(xap, xbp, xcp, image, 'B11');
  const B12 = do_ac(xap, xbp, xcp, image, 'B12');
  const sur = ee.Image([B1, B2, B3, B4, B5, B6, B7, B8, B8A, B11, B12, aot.multiply(3), tcwv.multiply(8), tco3]);
  return ee.Image(
    sur
      .copyProperties(image)
      .set('system:footprint', image.get('system:footprint'))
      .set('system:index', image.get('system:index'))
      .set('system:time_end', image.get('system:time_end'))
      .set('system:time_start', image.get('system:time_start')),
  );
};

const get_ozone_prior = (image) => {
  // take +- 3 days TOMS ozone data
  const image_date = image.date();
  let tco3 = ee
    .ImageCollection('TOMS/MERGED')
    .filterDate(ee.Date(image_date).advance(-3, 'day'), ee.Date(image_date).advance(3, 'day'))
    .mean();
  const start_year = ee.Date(image_date).advance(-3, 'year').get('year');
  const end_year = ee.Date(image_date).advance(3, 'year').get('year');
  const month = ee.Date(image_date).get('month');
  // use monthly data as the climatology to fill gaps
  const monthly_climatology = ee
    .ImageCollection('TOMS/MERGED')
    .filter(ee.Filter.calendarRange(start_year, end_year, 'year'))
    .filter(ee.Filter.calendarRange(month, month, 'month'))
    .median()
    .multiply(0.001);

  const ozone = ee.Image(
    ee.Algorithms.If(
      tco3.bandNames().length(),
      tco3.select('ozone').multiply(0.001),
      monthly_climatology.select('ozone'),
    ),
  );

  tco3 = ozone.unmask().where(ozone.mask().eq(0), monthly_climatology);
  //  to further fill the gaps....
  const ker = ee.Kernel.square(200, 'pixels');
  const tco3_mean = tco3
    .reduceNeighborhood({
      reducer: ee.Reducer.mean(),
      kernel: ker,
      inputWeight: 'mask',
      skipMasked: false,
      optimization: 'boxcar',
    })
    .rename('tco3');
  tco3 = tco3.unmask().where(tco3.mask().eq(0), tco3_mean);
  return tco3;
};

const s2_add_angs = (image) => {
  const saa = ee.Image(ee.Number(image.get('MEAN_SOLAR_AZIMUTH_ANGLE'))).rename('saa');
  const sza = ee.Image(ee.Number(image.get('MEAN_SOLAR_ZENITH_ANGLE'))).rename('sza');
  const vza_ang_1 = ee.List([
    ee.Number(image.get('MEAN_INCIDENCE_ZENITH_ANGLE_B1')),
    ee.Number(image.get('MEAN_INCIDENCE_ZENITH_ANGLE_B2')),
    ee.Number(image.get('MEAN_INCIDENCE_ZENITH_ANGLE_B3')),
    ee.Number(image.get('MEAN_INCIDENCE_ZENITH_ANGLE_B4')),
    ee.Number(image.get('MEAN_INCIDENCE_ZENITH_ANGLE_B5')),
    ee.Number(image.get('MEAN_INCIDENCE_ZENITH_ANGLE_B6')),
    ee.Number(image.get('MEAN_INCIDENCE_ZENITH_ANGLE_B7')),
    ee.Number(image.get('MEAN_INCIDENCE_ZENITH_ANGLE_B8')),
    ee.Number(image.get('MEAN_INCIDENCE_ZENITH_ANGLE_B8A')),
    ee.Number(image.get('MEAN_INCIDENCE_ZENITH_ANGLE_B9')),
    ee.Number(image.get('MEAN_INCIDENCE_ZENITH_ANGLE_B10')),
    ee.Number(image.get('MEAN_INCIDENCE_ZENITH_ANGLE_B11')),
    ee.Number(image.get('MEAN_INCIDENCE_ZENITH_ANGLE_B12')),
  ]);

  const vaa_ang_1 = ee.List([
    ee.Number(image.get('MEAN_INCIDENCE_AZIMUTH_ANGLE_B1')),
    ee.Number(image.get('MEAN_INCIDENCE_AZIMUTH_ANGLE_B2')),
    ee.Number(image.get('MEAN_INCIDENCE_AZIMUTH_ANGLE_B3')),
    ee.Number(image.get('MEAN_INCIDENCE_AZIMUTH_ANGLE_B4')),
    ee.Number(image.get('MEAN_INCIDENCE_AZIMUTH_ANGLE_B5')),
    ee.Number(image.get('MEAN_INCIDENCE_AZIMUTH_ANGLE_B6')),
    ee.Number(image.get('MEAN_INCIDENCE_AZIMUTH_ANGLE_B7')),
    ee.Number(image.get('MEAN_INCIDENCE_AZIMUTH_ANGLE_B8')),
    ee.Number(image.get('MEAN_INCIDENCE_AZIMUTH_ANGLE_B8A')),
    ee.Number(image.get('MEAN_INCIDENCE_AZIMUTH_ANGLE_B9')),
    ee.Number(image.get('MEAN_INCIDENCE_AZIMUTH_ANGLE_B10')),
    ee.Number(image.get('MEAN_INCIDENCE_AZIMUTH_ANGLE_B11')),
    ee.Number(image.get('MEAN_INCIDENCE_AZIMUTH_ANGLE_B12')),
  ]);
  const vza_ang_2 = ee.Image.constant(vza_ang_1.reduce(ee.Reducer.mean())).rename('vza');
  const vaa_ang_2 = ee.Image.constant(vaa_ang_1.reduce(ee.Reducer.mean())).rename('vaa');

  return image.addBands([sza.float(), saa.float(), vza_ang_2.float(), vaa_ang_2.float()]);
};

var get_best_aot = function (image) {
  var qa = image.select('AOD_QA');
  // BIT 8-11 is the AOD BIT
  var qa_mask = (1 << 8) + (1 << 9) + (1 << 10) + (1 << 11);
  var aod_qa = qa.bitwiseAnd(qa_mask).rightShift(8);
  var best_aot = aod_qa.eq(0);
  return image.updateMask(best_aot);
};

const get_aot_prior = (image) => {
  const image_date = ee.Date(image.get('system:time_start'));
  let mcd19_aot = ee
    .ImageCollection('MODIS/006/MCD19A2_GRANULES')
    .filterDate(image_date.advance(-60, 'minute'), image_date.advance(60, 'minute'))
    .map(get_best_aot)
    .select('Optical_Depth_055')
    .mean();
  let bad_image = ee.Image(0);
  bad_image = bad_image.updateMask(bad_image.neq(0)).float();
  mcd19_aot = ee.Algorithms.If(mcd19_aot.bandNames().length(), mcd19_aot, bad_image);
  mcd19_aot = ee.Image(mcd19_aot);

  mcd19_aot = mcd19_aot.updateMask(mcd19_aot.gt(0));

  let daily_mcd19_aot = ee
    .ImageCollection('MODIS/006/MCD19A2_GRANULES')
    .filterDate(image_date, image_date.advance(1, 'day'))
    .map(get_best_aot)
    .select('Optical_Depth_055')
    .mean();
  bad_image = ee.Image(0);
  bad_image = bad_image.updateMask(bad_image.neq(0)).float();
  daily_mcd19_aot = ee.Algorithms.If(daily_mcd19_aot.bandNames().length(), daily_mcd19_aot, bad_image);
  daily_mcd19_aot = ee.Image(daily_mcd19_aot);

  const dailymcd19_aot = daily_mcd19_aot.updateMask(daily_mcd19_aot.gt(0));
  let ker = ee.Kernel.square(10, 'pixels');
  const mcd19_aot_mean = mcd19_aot
    .reduceNeighborhood({
      reducer: ee.Reducer.mean(),
      kernel: ker,
      inputWeight: 'mask',
      skipMasked: false,
      optimization: 'boxcar',
    })
    .rename('aot');
  mcd19_aot = mcd19_aot.unmask().where(mcd19_aot.mask().eq(0), mcd19_aot_mean);
  mcd19_aot = mcd19_aot.where(mcd19_aot.lte(0), daily_mcd19_aot);
  mcd19_aot = mcd19_aot.updateMask(mcd19_aot.gt(0)).divide(1000);

  ker = ee.Kernel.square(200, 'pixels');
  const aot_prior_mean = mcd19_aot
    .reduceNeighborhood({
      reducer: ee.Reducer.mean(),
      kernel: ker,
      inputWeight: 'mask',
      skipMasked: false,
      optimization: 'boxcar',
    })
    .rename('aot');
  let aot_prior = mcd19_aot.unmask().where(mcd19_aot.mask().eq(0), aot_prior_mean);

  ker = ee.Kernel.square(10, 'pixels');
  aot_prior = aot_prior
    .reduceNeighborhood({
      reducer: ee.Reducer.mean(),
      kernel: ker,
      inputWeight: 'mask',
      skipMasked: false,
      optimization: 'boxcar',
    })
    .rename('aot');
  return aot_prior.updateMask(aot_prior.gt(0)).clip(image.geometry()).rename('aot_prior');
};

const get_wv_prior = (image) => {
  const image_date = ee.Date(image.get('system:time_start'));
  let mcd19_wv = ee
    .ImageCollection('MODIS/006/MCD19A2_GRANULES')
    .filterDate(image_date.advance(-60, 'minute'), image_date.advance(60, 'minute'))
    .map(get_best_aot)
    .select('Column_WV')
    .mean();

  let bad_image = ee.Image(0);
  bad_image = bad_image.updateMask(bad_image.neq(0)).float();
  mcd19_wv = ee.Algorithms.If(mcd19_wv.bandNames(), mcd19_wv, bad_image);
  mcd19_wv = ee.Image(mcd19_wv);
  mcd19_wv = mcd19_wv.updateMask(mcd19_wv.gt(0));

  let daily_mcd19_wv = ee
    .ImageCollection('MODIS/006/MCD19A2_GRANULES')
    .filterDate(image_date, image_date.advance(1, 'day'))
    .map(get_best_aot)
    .select('Column_WV')
    .mean();

  bad_image = ee.Image(0);
  bad_image = bad_image.updateMask(bad_image.neq(0)).float();
  daily_mcd19_wv = ee.Algorithms.If(daily_mcd19_wv.bandNames().length(), daily_mcd19_wv, bad_image);
  daily_mcd19_wv = ee.Image(daily_mcd19_wv);

  const dailymcd19_wv = daily_mcd19_wv.updateMask(daily_mcd19_wv.gt(0));
  let ker = ee.Kernel.square(10, 'pixels');
  const mcd19_wv_mean = mcd19_wv
    .reduceNeighborhood({
      reducer: ee.Reducer.mean(),
      kernel: ker,
      inputWeight: 'mask',
      skipMasked: false,
      optimization: 'boxcar',
    })
    .rename('wv');
  mcd19_wv = mcd19_wv.unmask().where(mcd19_wv.mask().eq(0), mcd19_wv_mean);
  mcd19_wv = mcd19_wv.where(mcd19_wv.lte(0), daily_mcd19_wv);
  mcd19_wv = mcd19_wv.updateMask(mcd19_wv.gt(0)).divide(1000);
  ker = ee.Kernel.square(200, 'pixels');
  const wv_prior_mean = mcd19_wv
    .reduceNeighborhood({
      reducer: ee.Reducer.mean(),
      kernel: ker,
      inputWeight: 'mask',
      skipMasked: false,
      optimization: 'boxcar',
    })
    .rename('wv');
  let wv_prior = mcd19_wv.unmask().where(mcd19_wv.mask().eq(0), wv_prior_mean);

  ker = ee.Kernel.square(10, 'pixels');
  wv_prior = wv_prior
    .reduceNeighborhood({
      reducer: ee.Reducer.mean(),
      kernel: ker,
      inputWeight: 'mask',
      skipMasked: false,
      optimization: 'boxcar',
    })
    .rename('wv');
  return wv_prior.updateMask(wv_prior.gt(0)).clip(image.geometry()).rename('wv_prior');
};

const get_wv_prior_v6 = function (image) {
  const image_date = ee.Date(image.get('system:time_start'));
  let mcd19_wv_v6 = ee
    .ImageCollection('MODIS/061/MCD19A2_GRANULES')
    .filterDate(image_date.advance(-60, 'minute'), image_date.advance(60, 'minute'))
    .map(get_best_aot)
    .select('Column_WV')
    .mean();

  let bad_image_v6 = ee.Image(0);
  bad_image_v6 = bad_image_v6.updateMask(bad_image_v6.neq(0)).float();
  mcd19_wv_v6 = ee.Algorithms.If(mcd19_wv_v6.bandNames().length(), mcd19_wv_v6, bad_image_v6);
  mcd19_wv_v6 = ee.Image(mcd19_wv_v6);

  mcd19_wv_v6 = mcd19_wv_v6.updateMask(mcd19_wv_v6.gt(0));

  let daily_mcd19_wv_v6 = ee
    .ImageCollection('MODIS/061/MCD19A2_GRANULES')
    .filterDate(image_date, image_date.advance(1, 'day'))
    .map(get_best_aot)
    .select('Column_WV')
    .mean();

  bad_image_v6 = ee.Image(0);
  bad_image_v6 = bad_image_v6.updateMask(bad_image_v6.neq(0)).float();
  daily_mcd19_wv_v6 = ee.Algorithms.If(daily_mcd19_wv_v6.bandNames().length(), daily_mcd19_wv_v6, bad_image_v6);
  daily_mcd19_wv_v6 = ee.Image(daily_mcd19_wv_v6);

  const dailymcd19_wv = daily_mcd19_wv_v6.updateMask(daily_mcd19_wv_v6.gt(0));
  let ker_v6 = ee.Kernel.square(10, 'pixels');
  const mcd19_wv_mean = mcd19_wv_v6
    .reduceNeighborhood({
      reducer: ee.Reducer.mean(),
      kernel: ker_v6,
      inputWeight: 'mask',
      skipMasked: false,
      optimization: 'boxcar',
    })
    .rename('wv');
  mcd19_wv_v6 = mcd19_wv_v6.unmask().where(mcd19_wv_v6.mask().eq(0), mcd19_wv_mean);
  mcd19_wv_v6 = mcd19_wv_v6.where(mcd19_wv_v6.lte(0), daily_mcd19_wv_v6);
  mcd19_wv_v6 = mcd19_wv_v6.updateMask(mcd19_wv_v6.gt(0)).divide(1000);
  ker_v6 = ee.Kernel.square(200, 'pixels');
  const wv_prior_mean = mcd19_wv_v6
    .reduceNeighborhood({
      reducer: ee.Reducer.mean(),
      kernel: ker_v6,
      inputWeight: 'mask',
      skipMasked: false,
      optimization: 'boxcar',
    })
    .rename('wv');
  let wv_prior_v6 = mcd19_wv_v6.unmask().where(mcd19_wv_v6.mask().eq(0), wv_prior_mean);

  ker_v6 = ee.Kernel.square(10, 'pixels');
  wv_prior_v6 = wv_prior_v6
    .reduceNeighborhood({
      reducer: ee.Reducer.mean(),
      kernel: ker_v6,
      inputWeight: 'mask',
      skipMasked: false,
      optimization: 'boxcar',
    })
    .rename('wv');
  return wv_prior_v6.updateMask(wv_prior_v6.gt(0)).clip(image.geometry()).rename('wv_prior');
};
const get_aot_prior_v6 = function (image) {
  const image_date = ee.Date(image.get('system:time_start'));
  let mcd19_aot_v6 = ee
    .ImageCollection('MODIS/061/MCD19A2_GRANULES')
    .filterDate(image_date.advance(-60, 'minute'), image_date.advance(60, 'minute'))
    .map(get_best_aot)
    .select('Optical_Depth_055')
    .mean();
  let bad_image_v6 = ee.Image(0);
  bad_image_v6 = bad_image_v6.updateMask(bad_image_v6.neq(0)).float();
  mcd19_aot_v6 = ee.Algorithms.If(mcd19_aot_v6.bandNames().length(), mcd19_aot_v6, bad_image_v6);
  mcd19_aot_v6 = ee.Image(mcd19_aot_v6);

  mcd19_aot_v6 = mcd19_aot_v6.updateMask(mcd19_aot_v6.gt(0));

  let daily_mcd19_aot_v6 = ee
    .ImageCollection('MODIS/061/MCD19A2_GRANULES')
    .filterDate(image_date, image_date.advance(1, 'day'))
    .map(get_best_aot)
    .select('Optical_Depth_055')
    .mean();
  bad_image_v6 = ee.Image(0);
  bad_image_v6 = bad_image_v6.updateMask(bad_image_v6.neq(0)).float();
  daily_mcd19_aot_v6 = ee.Algorithms.If(daily_mcd19_aot_v6.bandNames().length(), daily_mcd19_aot_v6, bad_image_v6);
  daily_mcd19_aot_v6 = ee.Image(daily_mcd19_aot_v6);

  const dailymcd19_aot_v6 = daily_mcd19_aot_v6.updateMask(daily_mcd19_aot_v6.gt(0));
  let ker = ee.Kernel.square(10, 'pixels');
  const mcd19_aot_v6_mean = mcd19_aot_v6
    .reduceNeighborhood({
      reducer: ee.Reducer.mean(),
      kernel: ker,
      inputWeight: 'mask',
      skipMasked: false,
      optimization: 'boxcar',
    })
    .rename('aot');
  mcd19_aot_v6 = mcd19_aot_v6.unmask().where(mcd19_aot_v6.mask().eq(0), mcd19_aot_v6_mean);
  mcd19_aot_v6 = mcd19_aot_v6.where(mcd19_aot_v6.lte(0), daily_mcd19_aot_v6);
  mcd19_aot_v6 = mcd19_aot_v6.updateMask(mcd19_aot_v6.gt(0)).divide(1000);

  ker = ee.Kernel.square(200, 'pixels');
  const aot_prior_mean = mcd19_aot_v6
    .reduceNeighborhood({
      reducer: ee.Reducer.mean(),
      kernel: ker,
      inputWeight: 'mask',
      skipMasked: false,
      optimization: 'boxcar',
    })
    .rename('aot');
  let aot_prior_v6 = mcd19_aot_v6.unmask().where(mcd19_aot_v6.mask().eq(0), aot_prior_mean);
  ker = ee.Kernel.square(10, 'pixels');
  aot_prior_v6 = aot_prior_v6
    .reduceNeighborhood({
      reducer: ee.Reducer.mean(),
      kernel: ker,
      inputWeight: 'mask',
      skipMasked: false,
      optimization: 'boxcar',
    })
    .rename('aot');
  return aot_prior_v6.updateMask(aot_prior_v6.gt(0)).clip(image.geometry()).rename('aot_prior');
};
